import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import { fetchNetworkExplorerClusters } from '@/api/networkExplorerApi';
import { buildAggregateClusterProperties } from '@/components/map/utils/entityClusterProperties';
import type { EntityToggleState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { showErrorToast } from '@/errors/showErrorToast';
import { POI_MIN_ZOOM } from '@/hooks/useEntitiesLayer';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

// Network Explorer's cluster layer: the wide end of the explorer map. The server bins every
// client's POI and leak entities into grid cells and sends one point per cell with the
// family totals and per-status counters; this source clusters those cells further with
// accumulators that SUM the same counters, so a bubble at any zoom reads the true entity
// count and the breakdown card reads the same keys as the single-client cluster card. Every
// feature on the source is an aggregate — a lone cell is drawn exactly like a cluster — so
// there is no pin layer and no "1" bubble.
//
// At POI_MIN_ZOOM the bubbles hand over to the individual markers of the shared entity
// pipeline (useEntitiesLayer, fed by the explorer's viewport feed) — the same boundary the
// single-client network→viewport split uses, enforced continuously by layer maxzoom and
// at the band level by the semantic-zoom registry.
//
// One fetch per explorer session: the cells feed has no viewport and no parameters.

export const EXPLORER_CELLS_SOURCE_ID = 'orbit-network-explorer-cells-source';

const EXPLORER_LAYER_IDS = [ORBIT_LAYER_IDS.explorerClusters, ORBIT_LAYER_IDS.explorerClusterCount];

// Same bubble styling as the single-client clusters so the two modes read as one product.
const BUBBLE_COLOUR = '#3b82f6';

type Expression = maplibregl.ExpressionSpecification;

/** The count a bubble shows under the current POI / Leak toggles. */
export function explorerCountExpression(toggles: EntityToggleState): Expression {
    if (toggles.pois && toggles.leaks) return ['get', 'count_total'];
    if (toggles.pois) return ['get', 'count_pois'];
    return ['get', 'count_leaks'];
}

function bubbleRadius(count: Expression): Expression {
    return ['step', count, 16, 100, 20, 1000, 24];
}

// The Figma shows whole numbers with thousands separators ("7,901"), not MapLibre's
// abbreviated point_count ("7.9k").
function bubbleLabel(count: Expression): Expression {
    return ['number-format', count, { locale: 'en-GB' }];
}

function nonEmpty(count: Expression): Expression {
    return ['>', count, 0];
}

/**
 * Re-points both layers at the counter the toggles select — no refetch, no source rebuild:
 * the cells carry every family's counts, so a toggle is a layout/paint change only. A
 * cluster with nothing to show under the chosen family is filtered away rather than drawn
 * as "0". Visibility is deliberately not written here: both-toggles-off and the semantic
 * band are useMapLayerVisibility's call for every POI-family layer, this one included, so
 * there is exactly one writer.
 */
function applyToggles(map: maplibregl.Map, toggles: EntityToggleState): void {
    const count = explorerCountExpression(toggles);
    try {
        if (map.getLayer(ORBIT_LAYER_IDS.explorerClusters)) {
            map.setPaintProperty(ORBIT_LAYER_IDS.explorerClusters, 'circle-radius', bubbleRadius(count));
            map.setFilter(ORBIT_LAYER_IDS.explorerClusters, nonEmpty(count));
        }
        if (map.getLayer(ORBIT_LAYER_IDS.explorerClusterCount)) {
            map.setLayoutProperty(ORBIT_LAYER_IDS.explorerClusterCount, 'text-field', bubbleLabel(count));
            map.setFilter(ORBIT_LAYER_IDS.explorerClusterCount, nonEmpty(count));
        }
    } catch {
        // Progressive enhancement — a mid-style-swap layer must not break the map.
    }
}

function registerSourceAndLayers(map: maplibregl.Map, data: FeatureCollection, toggles: EntityToggleState): void {
    try {
        if (map.getSource(EXPLORER_CELLS_SOURCE_ID)) {
            (map.getSource(EXPLORER_CELLS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            map.addSource(EXPLORER_CELLS_SOURCE_ID, {
                type: 'geojson',
                data,
                cluster: true,
                clusterRadius: 50,
                // The bubbles are never drawn from POI_MIN_ZOOM inward (layer maxzoom), so
                // the cells need not merge, or be tiled, any further than that.
                clusterMaxZoom: POI_MIN_ZOOM,
                maxzoom: POI_MIN_ZOOM + 1,
                clusterProperties: buildAggregateClusterProperties(),
            });
        }
    } catch {
        return;
    }

    const count = explorerCountExpression(toggles);

    if (!map.getLayer(ORBIT_LAYER_IDS.explorerClusters)) {
        try {
            addOrbitLayer(
                map,
                {
                    id: ORBIT_LAYER_IDS.explorerClusters,
                    type: 'circle',
                    source: EXPLORER_CELLS_SOURCE_ID,
                    filter: nonEmpty(count),
                    maxzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-color': BUBBLE_COLOUR,
                        'circle-radius': bubbleRadius(count),
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!map.getLayer(ORBIT_LAYER_IDS.explorerClusterCount)) {
        try {
            addOrbitLayer(
                map,
                {
                    id: ORBIT_LAYER_IDS.explorerClusterCount,
                    type: 'symbol',
                    source: EXPLORER_CELLS_SOURCE_ID,
                    filter: nonEmpty(count),
                    maxzoom: POI_MIN_ZOOM,
                    layout: {
                        'text-field': bubbleLabel(count),
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                    },
                    paint: { 'text-color': '#ffffff' },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    applyToggles(map, toggles);
}

/**
 * Registers the Network Explorer cells source and bubble layers, fetches the estate once,
 * and keeps the bubbles pointed at whichever family the POI / Leak toggles select. Inert
 * (no fetch, no layers) unless `enabled`; disabling empties the source so nothing lingers
 * if the map instance outlives the mode.
 */
export function useNetworkExplorerClustersLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    entityToggles: EntityToggleState,
    enabled: boolean,
): void {
    const dataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    const togglesRef = useRef(entityToggles);
    useEffect(() => {
        togglesRef.current = entityToggles;
    }, [entityToggles]);

    // One fetch per enabled session.
    useEffect(() => {
        dataRef.current = EMPTY_FEATURE_COLLECTION;
        const map = mapRef.current?.getMap();
        if (map) setGeoJsonSourceData(map, EXPLORER_CELLS_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!enabled || !isMapLoaded) return;

        const controller = new AbortController();
        fetchNetworkExplorerClusters(controller.signal)
            .then((data) => {
                if (controller.signal.aborted) return;
                dataRef.current = data;
                const current = mapRef.current?.getMap();
                if (current) setGeoJsonSourceData(current, EXPLORER_CELLS_SOURCE_ID, data);
            })
            .catch((err: unknown) => {
                if (!controller.signal.aborted) showErrorToast(err);
            });
        return () => {
            controller.abort();
        };
    }, [enabled, isMapLoaded, mapRef]);

    // Register source + layers; survive style reloads.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const map = mapRef.current?.getMap();
        if (!map) return;

        const register = () => {
            registerSourceAndLayers(map, dataRef.current, togglesRef.current);
        };

        register();
        map.on('style.load', register);

        const setPointer = () => {
            map.getCanvas().style.cursor = 'pointer';
        };
        const clearPointer = () => {
            map.getCanvas().style.cursor = '';
        };
        for (const layerId of EXPLORER_LAYER_IDS) {
            map.on('mousemove', layerId, setPointer);
            map.on('mouseleave', layerId, clearPointer);
        }

        return () => {
            map.off('style.load', register);
            for (const layerId of EXPLORER_LAYER_IDS) {
                map.off('mousemove', layerId, setPointer);
                map.off('mouseleave', layerId, clearPointer);
            }
        };
    }, [enabled, isMapLoaded, mapRef]);

    // The toggles are a display switch over the cached cells — never a refetch.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const map = mapRef.current?.getMap();
        if (!map) return;
        applyToggles(map, entityToggles);
    }, [enabled, isMapLoaded, mapRef, entityToggles]);
}
